import { createClient, SupabaseClient } from '@supabase/supabase-js'
import { Database, OrganizationId, OrganizationConfig, OrgClientInfo } from './types'

// Nexus Admin Supabase client (singleton)
// Use window to persist across HMR
declare global {
  interface Window {
    __nexusClient?: SupabaseClient<Database>
  }
}

let nexusClient: SupabaseClient<Database> | null =
  typeof window !== 'undefined' ? window.__nexusClient || null : null

// Cache for organization clients
const orgClientCache = new Map<OrganizationId, SupabaseClient>()

/**
 * Get the Nexus Admin Supabase client
 * This connects to the central admin database with organizations, admin_users, etc.
 */
export function getNexusClient(): SupabaseClient<Database> {
  if (nexusClient) {
    console.log('📦 Supabase: Returning cached client')
    return nexusClient
  }

  console.log('📦 Supabase: Creating new client...')
  const url = import.meta.env.VITE_NEXUS_SUPABASE_URL
  const key = import.meta.env.VITE_NEXUS_SUPABASE_ANON_KEY

  console.log('📦 Supabase: URL =', url ? url.substring(0, 30) + '...' : 'MISSING')
  console.log('📦 Supabase: Key =', key ? 'SET' : 'MISSING')

  if (!url || !key) {
    console.error('📦 Supabase: Missing credentials!')
    throw new Error('Missing Nexus Supabase credentials. Check VITE_NEXUS_SUPABASE_URL and VITE_NEXUS_SUPABASE_ANON_KEY')
  }

  nexusClient = createClient<Database>(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    },
  })

  // Store on window to prevent HMR duplicates
  if (typeof window !== 'undefined') {
    window.__nexusClient = nexusClient
  }

  console.log('📦 Supabase: Client created successfully')
  return nexusClient
}

/**
 * Create a Supabase client for a specific organization
 * Uses credentials stored in the organizations table
 */
export function createOrgClient(config: OrganizationConfig): SupabaseClient | null {
  if (!config.supabaseUrl || !config.supabaseAnonKey) {
    console.warn(`Organization ${config.slug} is not configured with Supabase credentials`)
    return null
  }

  // Check cache first
  const cached = orgClientCache.get(config.slug)
  if (cached) return cached

  const client = createClient(config.supabaseUrl, config.supabaseAnonKey, {
    auth: {
      persistSession: false, // Org clients don't need auth persistence
      autoRefreshToken: false,
    },
  })

  orgClientCache.set(config.slug, client)
  return client
}

/**
 * Get a cached org client by ID
 */
export function getOrgClient(orgId: OrganizationId): SupabaseClient | null {
  return orgClientCache.get(orgId) || null
}

/**
 * Clear the org client cache (useful when org credentials are updated)
 */
export function clearOrgClientCache(orgId?: OrganizationId) {
  if (orgId) {
    orgClientCache.delete(orgId)
  } else {
    orgClientCache.clear()
  }
}

/**
 * Get all configured org clients
 * Useful for aggregating data across all organizations
 */
export async function getAllOrgClients(): Promise<OrgClientInfo[]> {
  const nexus = getNexusClient()

  const { data: orgs, error } = await nexus
    .from('organizations')
    .select('*')
    .eq('is_active', true)
    .eq('is_configured', true)

  if (error || !orgs) {
    console.error('Failed to fetch organizations:', error)
    return []
  }

  const clients: OrgClientInfo[] = []

  for (const org of orgs as any[]) {
    const config: OrganizationConfig = {
      id: org.id,
      slug: org.slug as OrganizationId,
      name: org.name,
      icon: org.icon,
      color: org.color,
      description: org.description || '',
      supabaseUrl: org.supabase_url,
      supabaseAnonKey: org.supabase_anon_key,
      isActive: org.is_active,
      isConfigured: org.is_configured,
    }

    const client = createOrgClient(config)
    if (client) {
      clients.push({ orgId: config.slug, client, config })
    }
  }

  return clients
}
